import { isNetworkCreateError } from '@/utils/http/networkCreateError';
import { logger } from '@/utils/logger/logger';
import { type LedgerRecordingCompletion } from '@heidi/capture/ports';
import {
  cancelTracking,
  finishTracking,
  type SessionFlowFailure,
  startTracking,
  type TrackingHandle,
  TrackingKey,
} from '@heidi/runtime/performance';

// `recording_finalize` client metrics: one attempt per recording the client
// delivers after capture ends, matching the backend's one attempt per `:finish`.
// A pause ends a recording too. Autonomous after the stop or pause gesture, so
// no intent.
// Web: the attempt opens when capture ends with chunks to deliver and closes
// when the ledger settles every chunk and the finish step answers. A reload
// mid-delivery reports nothing: the ledger owns the pending delivery and
// recovery completes it on a later load. A delivery never recovered reports
// nothing, like the backend, which never sees its `:finish`.
// Desktop (Rust chunk upload): one stop IPC drains, backs up and calls `:finish`,
// so the attempt spans that call and closes when it answers.
// Not yet covered: the web legacy kit pipeline's latency and failed last chunk,
// the desktop legacy FE upload path, the desktop stops Rust or the session-end
// sweep issue on their own, the device-change segment stop, Chronicle stops,
// and the offline and bulk-sync legs, which finalize in Rust. The full list is
// in monitoring/rum_metrics.tf.

export type RecordingFinalizeErrorName =
  | 'RecordingFinalizeUploadIncomplete'
  | 'RecordingFinalizeFinishFailed'
  | 'RecordingFinalizeNetworkUnavailable'
  | 'RecordingFinalizeTimeout'
  | 'RecordingFinalizeServerError'
  | 'RecordingFinalizeRejected'
  | 'RecordingFinalizeRateLimited'
  | 'RecordingFinalizeForbidden'
  | 'RecordingFinalizeSessionDeleted'
  | 'RecordingFinalizeInterrupted'
  | 'RecordingFinalizeUnclassified';

/**
 * The `recording_finalize` failure vocabulary, registered with the rest of the
 * session-flow names. `RecordingFinalizeUnclassified` is absent on purpose, so
 * it lands in `_OTHER`.
 */
export const RECORDING_FINALIZE_FAILURE_TYPES: Readonly<
  Record<
    Exclude<RecordingFinalizeErrorName, 'RecordingFinalizeUnclassified'>,
    SessionFlowFailure
  >
> = {
  // Chunks settled without uploading, so the server is missing audio.
  RecordingFinalizeUploadIncomplete: {
    outcome: 'failed',
    errorType: 'upload_incomplete',
  },
  // The native stop or the stop marker reported a failed finish without a cause.
  RecordingFinalizeFinishFailed: {
    outcome: 'failed',
    errorType: 'finish_request_failed',
  },
  RecordingFinalizeNetworkUnavailable: {
    outcome: 'failed',
    errorType: 'network_unavailable',
  },
  RecordingFinalizeTimeout: { outcome: 'failed', errorType: 'request_timeout' },
  RecordingFinalizeServerError: { outcome: 'failed', errorType: 'server_error' },
  RecordingFinalizeRejected: { outcome: 'failed', errorType: 'request_rejected' },
  RecordingFinalizeRateLimited: { outcome: 'failed', errorType: 'rate_limited' },
  // Lost access to the session: a precondition, not a finalize fault.
  RecordingFinalizeForbidden: { outcome: 'blocked', errorType: 'permission_denied' },
  // The session was deleted while the recording was still delivering: a
  // `:finish` 404, or a session 404 the ledger reported as `sessionDeleted`.
  RecordingFinalizeSessionDeleted: { outcome: 'cancelled' },
  // Session expiry, logout or an abort ended the finish, not the finish itself.
  RecordingFinalizeInterrupted: { outcome: 'cancelled' },
};

const namedError = (name: RecordingFinalizeErrorName): Error => {
  const error = new Error(name);
  error.name = name;
  return error;
};

type CancelReason = 'session_deleted' | 'unauthorized' | 'aborted' | 'interrupted';

// A bounded name and a closed reason: no raw error, no free-form context.
type FailedClose = { name: RecordingFinalizeErrorName; cancelReason?: CancelReason };

/**
 * Maps a `:finish` rejection to a bounded name; an unknown cause lands in
 * `_OTHER`. The raw value is never forwarded: its name may mean something else
 * for another operation in the shared name map.
 */
export const classifyFinishRequestError = (error: unknown): FailedClose => {
  const { code, response } = (error ?? {}) as {
    code?: unknown;
    response?: { status?: unknown };
  };

  const status = response?.status;
  if (typeof status === 'number') {
    if (status === 401) {
      return { name: 'RecordingFinalizeInterrupted', cancelReason: 'unauthorized' };
    }
    if (status === 403) return { name: 'RecordingFinalizeForbidden' };
    // The session dependency answers 404; a missing stream is a 400.
    if (status === 404) {
      return { name: 'RecordingFinalizeSessionDeleted', cancelReason: 'session_deleted' };
    }
    if (status === 429) return { name: 'RecordingFinalizeRateLimited' };
    if (status === 408 || status === 504) return { name: 'RecordingFinalizeTimeout' };
    if (status >= 500) return { name: 'RecordingFinalizeServerError' };
    if (status >= 400) return { name: 'RecordingFinalizeRejected' };
  }

  if (code === 'ERR_CANCELED') {
    return { name: 'RecordingFinalizeInterrupted', cancelReason: 'aborted' };
  }
  if (code === 'ECONNABORTED' || code === 'ETIMEDOUT') {
    return { name: 'RecordingFinalizeTimeout' };
  }
  // Desktop offline short-circuits the request as an axios `ERR_NETWORK`.
  if (code === 'ERR_NETWORK' || isNetworkCreateError(error)) {
    return { name: 'RecordingFinalizeNetworkUnavailable' };
  }
  return { name: 'RecordingFinalizeUnclassified' };
};

// The step and the error's class only: never its message or stack.
const logStepFailure = (step: string, error: unknown): void =>
  logger.warn('[recordingFinalizeTracking] telemetry step failed', {
    step,
    errorName: error instanceof Error ? error.name : typeof error,
  });

// Telemetry must never change the delivery's outcome.
const safely = (step: string, run: () => void): void => {
  try {
    run();
  } catch (error) {
    logStepFailure(step, error);
  }
};

/** How the recording was finalized once every chunk settled. */
export type RecordingFinalizeVia =
  | 'finish_request'
  | 'stop_marker'
  | 'server_auto'
  | 'native_stop';

type CloseMetadata = {
  result: 'success' | 'error';
  finalize_via: RecordingFinalizeVia;
  gap_chunks?: number;
  cancel_reason?: CancelReason;
  backup_uploaded?: boolean;
  offline_handover?: boolean;
};

const closeAttempt = (
  step: string,
  handle: TrackingHandle | undefined,
  via: RecordingFinalizeVia,
  classify: () => FailedClose | undefined,
  extra: Omit<CloseMetadata, 'result' | 'finalize_via' | 'cancel_reason'> = {},
  endTime?: number
): void =>
  safely(step, () => {
    if (!handle) return;
    const timing = endTime === undefined ? {} : { endTime };
    // A throw inside classification still closes the attempt, as `_OTHER`,
    // instead of leaving it to replay as abandoned.
    let close: FailedClose | undefined;
    try {
      close = classify();
    } catch (error) {
      logStepFailure(`${step}_classify`, error);
      close = { name: 'RecordingFinalizeUnclassified' };
    }
    if (!close) {
      const metadata: CloseMetadata = { result: 'success', finalize_via: via, ...extra };
      finishTracking({ handle, metadata, ...timing });
      return;
    }
    const metadata: CloseMetadata = {
      result: 'error',
      finalize_via: via,
      ...extra,
      ...(close.cancelReason ? { cancel_reason: close.cancelReason } : {}),
    };
    finishTracking({
      handle,
      outcome: 'failed',
      error: namedError(close.name),
      metadata,
      ...timing,
    });
  });

// ── Web: the ledger delivery ────────────────────────────────────────────────

// Open until the finish step settles. A recording id is unique per capture.
const openAttempts = new Map<string, TrackingHandle>();

/** Capture ended with chunks to deliver: the attempt opens here. */
export const beginRecordingFinalizeAttempt = (recordingId: string): void =>
  safely('begin', () => {
    // A second capture end for one recording is a kit fault; keep the first.
    if (openAttempts.has(recordingId)) return;
    openAttempts.set(
      recordingId,
      startTracking({
        key: TrackingKey.SCRIBE_RECORDING_FINALIZE,
        instanced: true,
        // The ledger owns the pending delivery and recovery resumes it after a
        // reload, so a page exit is not an abandon: the recovered completion
        // reports the one attempt, flagged start_unobserved.
        metadata: { trigger: 'capture_end', outlives_page: true },
      })
    );
  });

export type RecordingFinalizePipeline = 'ledger' | 'legacy';

/**
 * A completion whose capture end this page never saw: ledger recovery after a
 * reload, or the legacy pipeline, which reports no capture end. It still
 * counts, with a near-zero duration flagged for latency panels to exclude.
 */
const takeOrOpenAttempt = (
  recordingId: string,
  pipeline: RecordingFinalizePipeline
): TrackingHandle => {
  const handle = openAttempts.get(recordingId);
  if (handle) {
    openAttempts.delete(recordingId);
    return handle;
  }
  return startTracking({
    key: TrackingKey.SCRIBE_RECORDING_FINALIZE,
    instanced: true,
    metadata: {
      trigger: pipeline === 'ledger' ? 'recovery' : 'legacy_pipeline',
      start_unobserved: true,
    },
  });
};

export type LedgerFinalizeResult = {
  via: Exclude<RecordingFinalizeVia, 'native_stop'>;
  pipeline: RecordingFinalizePipeline;
  /** Chunks that settled without uploading: failed or abandoned, audio is missing. */
  gaps: LedgerRecordingCompletion['gaps'];
  /**
   * The ledger saw a session 404 mid-delivery. v2 and v3 have no `:finish` to
   * answer 404, so this is how they learn the session is gone.
   */
  sessionDeleted: boolean;
  /** The `:finish` request's rejection. */
  error?: unknown;
  /**
   * The stop marker could not be posted after its retries, or `:finish`
   * answered that it did not finalize (`is_success: false`).
   */
  finishFailed?: boolean;
  /**
   * The legacy HTTP handler resolved `:finish` with no response, after a
   * dead-session logout or a suspended-account redirect. Nothing finalized.
   */
  interrupted?: boolean;
};

/**
 * Every chunk settled and the finish step answered: the attempt closes. A
 * deleted session (a `:finish` 404 or `sessionDeleted`) or an interrupted
 * request outranks a gapped delivery, which outranks the finish step's own
 * fault: missing audio is the worse outcome of a finalize.
 */
export const completeRecordingFinalizeAttempt = (
  recordingId: string,
  result: LedgerFinalizeResult
): void =>
  safely('complete', () => {
    const handle = takeOrOpenAttempt(recordingId, result.pipeline);
    const gapChunks = result.gaps.failed + result.gaps.abandoned;
    closeAttempt(
      'complete',
      handle,
      result.via,
      () => {
        if (result.interrupted) {
          return { name: 'RecordingFinalizeInterrupted', cancelReason: 'interrupted' };
        }
        const requestFailure =
          result.error === undefined
            ? undefined
            : classifyFinishRequestError(result.error);
        if (requestFailure?.cancelReason) return requestFailure;
        if (result.sessionDeleted) {
          return {
            name: 'RecordingFinalizeSessionDeleted',
            cancelReason: 'session_deleted',
          };
        }
        if (gapChunks > 0) return { name: 'RecordingFinalizeUploadIncomplete' };
        if (requestFailure) return requestFailure;
        if (result.finishFailed) return { name: 'RecordingFinalizeFinishFailed' };
        return undefined;
      },
      gapChunks > 0 ? { gap_chunks: gapChunks } : {}
    );
  });

// ── Desktop: the Rust stop IPC ──────────────────────────────────────────────

export type NativeFinalizeSegment = 'stop' | 'pause';

/** Opens before the stop IPC. Returns no handle if telemetry threw. */
export const beginNativeRecordingFinalizeAttempt = (
  segment: NativeFinalizeSegment
): TrackingHandle | undefined => {
  let handle: TrackingHandle | undefined;
  safely('native_begin', () => {
    handle = startTracking({
      key: TrackingKey.SCRIBE_RECORDING_FINALIZE,
      instanced: true,
      metadata: { trigger: 'native_stop', segment },
    });
  });
  return handle;
};

/** Capture never stopped, so no finalize ran: drop the attempt unreported. */
export const cancelNativeRecordingFinalizeAttempt = (
  handle: TrackingHandle | undefined
): void =>
  safely('native_cancel', () => {
    if (handle) cancelTracking({ handle });
  });

export type NativeFinalizeResult = {
  /** The stop IPC rejected after its retries and no outcome was recovered. */
  stopFailed: boolean;
  failedChunks?: number;
  finishSucceeded?: boolean;
  backupUploaded?: boolean;
  /** Unsent audio went to the offline queue, whose sync finalizes later. */
  offlineHandover: boolean;
};

/**
 * The stop IPC settled: the attempt closes on what Rust reported. A handover
 * outranks the failed chunks it explains; missing audio outranks the finish.
 * `endTime` is when the IPC answered, so the handover that follows it does not
 * stretch the span.
 */
export const completeNativeRecordingFinalizeAttempt = (
  handle: TrackingHandle | undefined,
  result: NativeFinalizeResult,
  endTime?: number
): void =>
  closeAttempt(
    'native_complete',
    handle,
    'native_stop',
    () => {
      // Nothing came back, so nothing says whether the finish ran.
      if (result.stopFailed) return { name: 'RecordingFinalizeUnclassified' };
      if (result.offlineHandover) return { name: 'RecordingFinalizeNetworkUnavailable' };
      if ((result.failedChunks ?? 0) > 0)
        return { name: 'RecordingFinalizeUploadIncomplete' };
      if (result.finishSucceeded !== true)
        return { name: 'RecordingFinalizeFinishFailed' };
      return undefined;
    },
    {
      ...(result.backupUploaded === undefined
        ? {}
        : { backup_uploaded: result.backupUploaded }),
      ...(result.offlineHandover ? { offline_handover: true } : {}),
      ...((result.failedChunks ?? 0) > 0 ? { gap_chunks: result.failedChunks } : {}),
    },
    endTime
  );

export const __resetRecordingFinalizeTrackingForTests = (): void => {
  openAttempts.clear();
};
